import {
  Component,
  ChangeDetectorRef,
  OnInit,
  ViewChild,
  signal,
  inject,
  WritableSignal,
  computed,
  effect,
  ChangeDetectionStrategy,
} from '@angular/core'
import { Voucher } from 'depoto-core/src/entities'
import { ListingItem } from 'depoto-core/src/models'
import { BaseListing } from '../_base-listing/base-listing'
import { ExportsComponent } from '../exports/exports.component'
import { CoreService } from '../../services'
import { SchemaUtil, withDateBoundary } from '../../utils'
import { vouchersColumns } from '../../utils/definitions.util'
import { injectInfiniteQuery } from '@tanstack/angular-query-experimental'
import { VoucherService } from '../../services/entity-services/voucher.service'

const DEFAULT_FILTERS = {
  name: null,
  code: null,
  discountType: 'all',
  isValid: 'all',
  isUsed: 'all',
  enabled: 'all',
  validDate: {
    right_date: '',
    left_date: '',
  },
}

@Component({
  selector: 'app-vouchers',
  templateUrl: './vouchers.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class VouchersComponent extends BaseListing implements OnInit {
  private voucherService = inject(VoucherService)
  @ViewChild('exportsModal') exportsModal: ExportsComponent
  listingColumns: WritableSignal<ListingItem[]> = signal(vouchersColumns)
  // listingColumns = signal(vouchersColumns)
  // storageKey: WritableSignal<{ type: string; key: string }> = signal({ type: '', key: '' })

  public vouchers: Voucher[] = []

  sortProp = signal('name')
  sortDir = signal('asc')
  public discountTypeOptions = [
    {
      id: 'all',
      name: 'all',
    },
    {
      id: 'percentage',
      name: 'voucher.discountType.percentage',
    },
    {
      id: 'value',
      name: 'voucher.discountType.value',
    },
  ]
  public isValidOptions = [
    {
      id: 'all',
      name: 'all',
    },
    {
      id: true,
      name: 'yes',
    },
    {
      id: false,
      name: 'no',
    },
  ]
  public enabledOptions = [
    {
      id: 'all',
      name: 'all',
    },
    {
      id: true,
      name: 'yes',
    },
    {
      id: false,
      name: 'no',
    },
  ]
  public isUsedOptions = [
    {
      id: 'all',
      name: 'all',
    },
    {
      id: true,
      name: 'yes',
    },
    {
      id: false,
      name: 'no',
    },
  ]

  public currentPage = 1
  public endPage = 1
  filtersExpanded = signal(false)
  readonly defaultFilters = DEFAULT_FILTERS
  readonly advancedFilterKeys = ['enabled', 'isValid', 'isUsed', 'validDate']

  filters = signal(this.loadViewSettings('vouchers')?.filters || structuredClone(DEFAULT_FILTERS))

  readonly queryFilters = computed(() => {
    const filtersWithValues = {}
    for (const [key, value] of Object.entries(this.filters())) {
      // if it has value
      if (
        value !== undefined &&
        value !== null &&
        value !== false &&
        value !== 0 &&
        value !== '' &&
        value !== 'all' &&
        (!Array.isArray(value) || (Array.isArray(value) && value?.length))
      ) {
        if (value === 'yes' || value === 'no') {
          // boolean
          filtersWithValues[key] = value === 'yes'
        } else if (Array.isArray(value) && value[0]?.id) {
          // array
          filtersWithValues[key] = value.map(object => object?.id)
        } else if (key === 'code' && !isNaN(+value)) {
          // array
          filtersWithValues[key] = Number(value)
        } else if (key === 'validDate') {
          const left = (value as any)?.left_date
          const right = (value as any)?.right_date
          if ((left && left.length) || (right && right.length)) {
            filtersWithValues[key] = {
              ...(left?.length && {
                left_date: left,
              }),
              ...(right?.length && {
                right_date: withDateBoundary(right, 'end'),
              }),
            }
          }
        } else {
          // other
          filtersWithValues[key] = value
        }
      }
    }
    return filtersWithValues
  })

  readonly queryVouchers = injectInfiniteQuery(() => ({
    queryKey: ['vouchers', { sortProp: this.sortProp(), sortDir: this.sortDir() }], // todo add  queryKey -> sort, filters, schema - columns
    queryFn: async ({ pageParam }) => {
      // const requestId = ++this.currentRequestId // unique ID per call
      // todo. disable for now - querySelectorAll gets all inputs from pages, even when they should be destroyed. (looking at you, Products form fields!)
      return await this.voucherService
        .getVouchers(
          {
            page: pageParam,
            sort: this.sortProp(),
            direction: this.sortDir(),
            filters: this.queryFilters(),
          },
          SchemaUtil.voucher
        )
        .then(result => {
          this.saveViewSettings('vouchers', {
            filters: this.filters(),
            sortProp: this.sortProp(),
            sortDir: this.sortDir(),
          })
          return result
        })
    },
    initialPageParam: 1,
    getNextPageParam: result => {
      return Number(result?.paginator?.current) < Number(result?.paginator?.endPage)
        ? Number(result?.paginator?.current) + 1
        : undefined
    },
  }))

  totalItems = computed(() => this.queryVouchers.data()?.pages?.[0]?.paginator?.totalCount ?? 0)

  // readonly isPendingVouchers = computed(() => this.queryVouchers.isPending())
  // readonly isRefetchingVouchers = computed(() => this.queryVouchers.isRefetching())

  constructor(
    protected core: CoreService,
    protected changeDetector: ChangeDetectorRef
  ) {
    super(core)
    effect(() => {
      this.queryVouchers.data()
    })
    // this.getStorageKey()
  }

  ngOnInit(): void {
    const view = this.loadViewSettings('vouchers')
    this.filters.set(view?.filters || structuredClone(DEFAULT_FILTERS))
    this.sortProp.set(view?.sortProp || 'name')
    this.sortDir.set(view?.sortDir || 'asc')
    this.loadSorting('vouchers')
  }

  clearFilters() {
    this.filters.set(structuredClone(DEFAULT_FILTERS))
    this.queryVouchers.refetch() // todo
  }

  setFilters(property: string, value: any, dateLeftRight?: string) {
    if (dateLeftRight) {
      this.filters.set({
        ...this.filters(),
        [property]: {
          ...this.filters()[property],
          [dateLeftRight]: value,
        },
      })
    } else {
      this.filters.set({
        ...this.filters(),
        [property]: value,
      })
    }
  }

  protected readonly DEFAULT_FILTERS = DEFAULT_FILTERS
}
